import React, {
    FC,
    useEffect,
    useState,
} from "react"
import {
    Alert,
    Badge,
    Button,
    Card,
    Form,
    ListGroup,
    Spinner,
} from "react-bootstrap"
import axios from "axios"
import {useTranslation} from "react-i18next"

import {useAuth} from "../../../context/AuthProvider"
import {
    getTenantLabel,
    TenantPersonSearchResult,
    TenantTreeNode,
} from "./tenant.types"

type PersonTenantSearchProps = {
    onTenantSelect: (tenantId: string) => void
    onTenantManage?: (tenantId: string) => void
    onHighlight: (tenantIds: string[]) => void
    tenants?: TenantTreeNode[]
}

const PersonTenantSearch: FC<PersonTenantSearchProps> = ({
    onTenantSelect,
    onTenantManage,
    onHighlight,
    tenants = [],
}) => {
    const auth = useAuth()
    const {t} = useTranslation("examManagement")

    const [query, setQuery] = useState<string>("")
    const [results, setResults] =
        useState<TenantPersonSearchResult[]>([])
    const [loading, setLoading] =
        useState<boolean>(false)
    const [error, setError] =
        useState<boolean>(false)

    const normalizedQuery = query.trim().toLocaleLowerCase()
    const tenantResults = normalizedQuery.length < 2
        ? []
        : tenants.filter(tenant =>
            [tenant.describer, tenant.name?.de, tenant.name?.en,
                tenant.fullName?.de, tenant.fullName?.en]
                .filter(Boolean)
                .some(value => value!.toLocaleLowerCase().includes(normalizedQuery))
        )

    useEffect(() => {
        const trimmed = query.trim()

        if (trimmed.length < 2) {
            setResults([])
            setError(false)
            onHighlight([])
            return
        }

        onHighlight(tenantResults.map(tenant => tenant._id))

        const timer = window.setTimeout(() => {
            setLoading(true)

            axios
                .get(
                    "/api/tenant/searchByUser",
                    {
                        ...auth.axiosAuthHeader(),
                        params: {
                            q: trimmed,
                        },
                    }
                )
                .then(response => {
                    const loadedResults =
                        Array.isArray(response.data)
                            ? response.data
                            : []

                    setResults(loadedResults)
                    setError(false)

                    const tenantIds: string[] = []

                    loadedResults.forEach(
                        (result: TenantPersonSearchResult) => {
                            result.accesses.forEach(access => {
                                tenantIds.push(access.tenant._id)

                                if (access.department) {
                                    tenantIds.push(
                                        access.department._id
                                    )
                                }
                            })

                            result.memberships?.forEach(tenant => {
                                tenantIds.push(tenant._id)
                            })
                        }
                    )

                    onHighlight(
                        Array.from(new Set([
                            ...tenantIds,
                            ...tenantResults.map(tenant => tenant._id),
                        ]))
                    )
                })
                .catch(() => {
                    setResults([])
                    setError(true)
                    onHighlight(
                        tenantResults.map(tenant => tenant._id)
                    )
                })
                .finally(() => {
                    setLoading(false)
                })
        }, 350)

        return () => window.clearTimeout(timer)
    }, [query, tenants])

    return (
        <Card className="mb-3">
            <Card.Header>
                <strong>
                    <i className="bi bi-person-search me-2"/>
                    {t("search.title")}
                </strong>
            </Card.Header>

            <Card.Body>
                <Form.Control
                    type="search"
                    value={query}
                    placeholder={t("search.placeholder")}
                    onChange={event => setQuery(event.target.value)}
                />

                {loading && (
                    <div className="mt-3">
                        <Spinner size="sm" className="me-2"/>
                        {t("search.loading")}
                    </div>
                )}

                {error && (
                    <Alert variant="danger" className="mt-3 mb-0">
                        {t("search.error")}
                    </Alert>
                )}

                {!loading &&
                query.trim().length >= 2 &&
                !error &&
                results.length === 0 && tenantResults.length === 0 && (
                    <Alert variant="info" className="mt-3 mb-0">
                        {t("search.empty")}
                    </Alert>
                )}

                {tenantResults.length > 0 && (
                    <ListGroup className="mt-3">
                        {tenantResults.map(tenant => (
                            <ListGroup.Item
                                key={tenant._id}
                                className="d-flex justify-content-between align-items-center gap-2"
                            >
                                <div>
                                    <strong>{tenant.describer}</strong>
                                    <div>{getTenantLabel(tenant)}</div>
                                </div>
                                <div className="d-flex flex-wrap gap-1">
                                    <Button size="sm" variant="outline-primary"
                                            onClick={() => onTenantSelect(tenant._id)}>
                                        {t("search.selectTree")}
                                    </Button>
                                    {onTenantManage && (
                                        <Button size="sm" onClick={() => onTenantManage(tenant._id)}>
                                            {t("search.openManagement")}
                                        </Button>
                                    )}
                                </div>
                            </ListGroup.Item>
                        ))}
                    </ListGroup>
                )}

                {results.map(result => (
                    <Card
                        key={result.user._id}
                        className="mt-3"
                    >
                        <Card.Body>
                            <strong>
                                {result.user.given_name}
                                {" "}
                                {result.user.family_name}
                            </strong>

                            <div className="text-muted">
                                {result.user.email ||
                                result.user.preferred_username ||
                                result.user.esi}
                            </div>

                            <ListGroup className="mt-2">
                                {result.accesses.map(access => (
                                    <ListGroup.Item
                                        key={[
                                            result.user._id,
                                            access.tenant._id,
                                            access.role,
                                        ].join("-")}
                                        className="d-flex justify-content-between align-items-center gap-2"
                                    >
                                        <div>
                                            <div>
                                                {getTenantLabel(
                                                    access.tenant
                                                )}
                                            </div>

                                            <div className="d-flex flex-wrap gap-1 mt-1">
                                                <Badge bg="secondary">
                                                    {access.role}
                                                </Badge>

                                                {access.department &&
                                                access.department._id !==
                                                access.tenant._id && (
                                                    <Badge bg="info">
                                                        {getTenantLabel(
                                                            access.department
                                                        )}
                                                    </Badge>
                                                )}
                                            </div>
                                        </div>

                                        <div className="d-flex flex-wrap gap-1">
                                            <Button
                                                size="sm"
                                                variant="outline-primary"
                                                onClick={() => {
                                                    onTenantSelect(
                                                        access.tenant._id
                                                    )
                                                }}
                                            >
                                                {t("search.selectTree")}
                                            </Button>

                                            {onTenantManage && (
                                                <Button
                                                    size="sm"
                                                    onClick={() => {
                                                        onTenantManage(
                                                            access.tenant._id
                                                        )
                                                    }}
                                                >
                                                    {t("search.openManagement")}
                                                </Button>
                                            )}
                                        </div>
                                    </ListGroup.Item>
                                ))}
                                {result.memberships?.map(tenant => (
                                    <ListGroup.Item
                                        key={`${result.user._id}-${tenant._id}-membership`}
                                        className="d-flex justify-content-between align-items-center gap-2"
                                    >
                                        <div>
                                            <div>{getTenantLabel(tenant)}</div>
                                            <Badge bg="light" text="dark">
                                                {t("search.membershipOnly")}
                                            </Badge>
                                        </div>
                                        <div className="d-flex flex-wrap gap-1">
                                            <Button size="sm" variant="outline-primary"
                                                onClick={() => onTenantSelect(tenant._id)}>
                                                {t("search.selectTree")}
                                            </Button>
                                            {onTenantManage && (
                                                <Button size="sm" onClick={() => onTenantManage(tenant._id)}>
                                                    {t("search.openManagement")}
                                                </Button>
                                            )}
                                        </div>
                                    </ListGroup.Item>
                                ))}
                            </ListGroup>
                        </Card.Body>
                    </Card>
                ))}
            </Card.Body>
        </Card>
    )
}

export default PersonTenantSearch
